// Design doc: https://playgroundsessions.atlassian.net/wiki/spaces/DEV/pages/312803333/Note+Evaluation

import Note from "../../models/soundslice/Note";
import IPlayerNote from "../../models/scorer/IPlayerNote";
import {INoteScoreCriteria, IScoredNote} from "../../models/scorer/scorerTypes";
import {generateUUID} from "../misc";
import {
  emitNote_changed,
  soundSliceSdk_clearTempNotes,
  soundSliceSdk_drawTempNote,
} from "../eventBus/soundslice/soundSliceSdk";
import {
  IDisplayNoteFeedbackChanged,
  IPlayback_ended,
  IPlayback_reset,
  IPlaybackStarted,
  IPlayerNotePressed,
  IPlayerNoteReleased,
  NotesEventTypes
} from "../eventBus/notes/NotesEventTypes";
import {subscribeToNotesEvents} from "../eventBus/notes/notesEventBus";
import Segment from "../../models/view/lessonPage/Segment";
import {emitNotesScoring_completed} from "../eventBus/scorer/scorerEventBus";
import {SegmentModeEnum} from "../../models/view/lessonPage/SegmentModeEnum";
import {logger} from "../log/logger";
import noteScorerColors from "./noteScorerColors";
import thresholdSettings from "./threshholdSettings";
import {NoteEventTime} from "@playground-sessions/pitch-detection/types/toMidi";
import settingsService from "../settings/settingsService";
import workerFunction from "./noteScorerWorker";
import {midiNumberToNoteName} from "../lesson/soundUtilities";
import {getAudioContext} from "../audioContext";
import {lessonPlayerStore} from "../../redux/lesson/LessonPlayerStore";
import {settingsStore} from "../../redux/settings/settingsStore";
import {ILogArguments} from "../log/ILogArguments";

const noteScoreIntervalMs = 50;

interface IPromiseHash {
  [key: string]: {resolve: (data: any)=> void; reject: (errorMessage: string)=> void},
}

/**
 * Responsible for orchestrating the evaluation of whether notes are played correctly by the user.
 * Actual scoring occurs in the lessonScoringWorker. Score results are sent back to this service.
 *
 * Scores each note in the segment by evaluating notes played by the user.
 * There are windows of time where the user can play the note correctly, incorrectly, or closely.  The window duration
 * depends on how difficult the lesson is, and the tempo.
 *
 * Currently will only score whether the note was played in time, but does not score when the note was released.
 * Tie end notes are scored by seeing if the note is held/played by the user when the cursor gets to the beginning of
 * the tie end.
 *
 * All work is done by a worker thread to ensure performance.
 *
 * Legacy code:
 * https://github.com/PlaygroundSessions/PlaygroundZend/blob/master/public/admin/html/proof-of-concept/soundslice.html#L763
 * https://github.com/PlaygroundSessions/PlaygroundDevKit/blob/37c2bbad2e9e040fe88163cae2f13ca181200bae/src/com/playground/manager/PerformanceEvaluationManager.as
 * https://github.com/PlaygroundSessions/PlaygroundDevKit/blob/37c2bbad2e9e040fe88163cae2f13ca181200bae/src/com/playground/manager/ScoringManager.as
 * https://github.com/PlaygroundSessions/PlaygroundDevKit/blob/37c2bbad2e9e040fe88163cae2f13ca181200bae/src/com/playground/model/SongScoringModel.as
 */
class NoteScorer {
  #scoringWorker: Worker;
  #unsubscribeFuncs: (() => void)[];
  #notesInSegment: Note[] = [];
  #promiseHash: IPromiseHash = {} as IPromiseHash;
  #numberOfTracks: number; //how many tracks/clefs there are, which is used to determine where to draw extraneous notes played by the user.
  #segment: Segment | undefined;
  #displayNoteFeedback = true;
  #didPlayerPlayAnyNotes = false;//don't show score modal or record practice record if the user didn't play any notes.

  constructor() {
    this.#scoringWorker = createScoringWorkerFromFunction(workerFunction);
    this.#setupWorkerMessageRouter();
    this.#setupNotesEventRouter();
  }

  destroy(){
    this.#unsubscribeFuncs.forEach(u => u());
  }

  #setupWorkerMessageRouter(): void {
    this.#scoringWorker.onmessage = (e) => this.#routeWorkerMessage(e);
  }

  #setupNotesEventRouter(): void {
    this.#unsubscribeFuncs = [
      subscribeToNotesEvents(this.#routeNotesEvent.bind(this)), //listen for when playback starts and ends so we can begin scoring.
    ];
  }

  /**
   * This method routes events that can happen while a user is playing a segment.
   */
  #routeNotesEvent(e: NotesEventTypes){
    switch (e.type) {
      case "playbackStarted":
        //scoring is begun when notesPlayer signals that notes have begun to be played (after count in)
        this.#onPlaybackStartedEventReceived(e);
        break;
      case "playback_reset": // fall through to playback_ended
      case "playback_ended":
        this.#onPlayback_ended(e);
        break;
      case "displayNoteFeedbackChanged":
        this.#onDisplayNoteFeedback_changed(e);
        break;
      case "playerNotePressed":
        this.#onPlayerNote_pressed(e);
        break;
      case "playerNoteReleased":
        this.#onPlayerNote_released(e);
        break;
      case "clearAllNoteFeedback":
        this.#onAllNoteFeedback_cleared();
        break;
    }
  }

  #onPlaybackStartedEventReceived(e: IPlaybackStarted): void {
    this.#beginScoring(
      e.segment,
      e.speed,
      e.speedRelativeToRecordingIndex,
      e.notesInSegment,
      e.seekOffsetSeconds,
      e.loopEndOffsetSeconds,
      e.shouldClearScoredNotes
    );
  }

  #onPlayback_ended(e: IPlayback_ended | IPlayback_reset): void {
    const isPitchDetectionEnabled = lessonPlayerStore.getIsPitchDetectionEnabled();
    if (!isPitchDetectionEnabled) {
      this.#endScoringProcess(e.waitForScoringToComplete);
    }
  }

  #onDisplayNoteFeedback_changed(e: IDisplayNoteFeedbackChanged): void {
    this.#displayNoteFeedback = e.displayNoteFeedback;
    if(!this.#displayNoteFeedback){
      emitUiEventsToResetAllEditedNotesAndTempNotes(this.#notesInSegment);
    }
  }

  #onPlayerNote_pressed(e: IPlayerNotePressed) {
    // selectedMidiDeviceId corresponds to an actual midi input device whose value has been normalized.
    const selectedMidiDeviceId = settingsStore.getSelectedMidiDeviceId();
    const rawMidiDeviceId = e.deviceId;

    // Guard: make sure note came from the selected midi device or the virtual keyboard
    if ((e.normalizedMidiInput?.getId() !== selectedMidiDeviceId.toString()) && (rawMidiDeviceId !== "virtualKeyboard")) {
      logger.midiNoteAction.info({
        message: `noteScorer: Note detected from wrong device. Ignoring. Device expected: ${selectedMidiDeviceId}, device found: ${e.normalizedMidiInput?.getId()}`,
      });

      return;
    }

    this.#didPlayerPlayAnyNotes = true;

    // This is an experimental change to see if it helps with pitch detection timing issues.
    // If it doesn't work, swap which of the 2 lines below is commented out.
    // const performanceNowOffset = performance.now() - e.receivedTime;
    const performanceNowOffset = 0;

    const isPitchDetectionDebuggingEnabled = settingsStore.getIsPitchDetectionDebuggingEnabled();

    if (isPitchDetectionDebuggingEnabled) {
      const scoringStartTimeInMs = lessonPlayerStore.getScoringStartTimeInMs();
      if (scoringStartTimeInMs) {
        const playTime = getAudioContext().currentTime * 1000 - scoringStartTimeInMs;
        console.log("Played note: ", midiNumberToNoteName(e.midiNumber), " at ", playTime);
      }
    }

    return this.#scoringWorker.postMessage({
      method: 'handlePlayerNotePlayed',
      midi: e.midiNumber,
      velocity: e.velocity,
      source: e.source,
      receivedTime: e.receivedTime,
      id: generateUUID(),
      performanceNowOffset
    });
  }

  #onPlayerNote_released(e: IPlayerNoteReleased) {
    return this.#scoringWorker.postMessage({
      method: "handlePlayerNoteReleased",
      midi: e.midiNumber,
      source: e.source,
      receivedTime: e.receivedTime,
    });
  }

  onIsPitchDetectionEnabled_changed(isPitchDetectionEnabled: boolean): void {
    this.#scoringWorker.postMessage({
      method: "isPitchDetectionEnabled_changed",
      isPitchDetectionEnabled: isPitchDetectionEnabled,
    });
  }

  #onAllNoteFeedback_cleared() {
    emitUiEventsToResetAllEditedNotesAndTempNotes(this.#notesInSegment);
  }

  #routeWorkerMessage(e: MessageEvent){
    const method = e.data.method;
    switch(method){
      case `scoreInterval_completed`:
        // console.log("scoreInterval_completed message received");
        // This message is received every 50ms while scoring is in progress. If pitch detection is enabled, it will be called once
        // just before the "score" message is sent.
        this.#onScoreInterval_completed(e);
        break;
      case 'score':
        // console.log("score message received");
        // This message is received after all scoring has been completed.
        this.#onScoreMessage_received(e);
        break;
      case 'detectedNotesScoring_completed':
        console.log("noteScorer: detectedNotesScoring_completed");
        this.#endScoringProcess(true);
        break;
      case 'noteScorerWorkerLog':
        this.#logForNoteScorerWorker(e.data.payload);
        break;
    }
  }

  #logForNoteScorerWorker(payload: ILogArguments): void {
    logger.noteScorerWorker.info(payload);
  }

  /**
   * This method gets called whenever an interval has been scored. FYI: the score interval method is called every 50ms.
   */
  #onScoreInterval_completed(e: MessageEvent): void {
    const shouldShowFeedback = lessonPlayerStore.getIsInstantFeedbackEnabled();

    if (shouldShowFeedback) {
      emitUiEventsForNotes(e.data.scoredNotes, this.#notesInSegment, this.#numberOfTracks)
    } else {
      //TODO: this probably shouldn't be here. Doesn't seem like it need to be cleared every 50 ms.
      soundSliceSdk_clearTempNotes();
    }
  }

  #onScoreMessage_received(e: MessageEvent): void {
    this.#handleWorkerScore(e.data.requestId,
      e.data.scoredNotes,
      e.data.unscoredNotesCriteriaInSelection,
      e.data.unscoredPlayerNotes,
      e.data.speed,
      e.data.scoringStartTime,
      e.data.scoringEndTime,
      e.data.loopEndOffsetSeconds,
      e.data.seekOffsetSeconds
    );
  }

  scoreNotes(notes: NoteEventTime[]): void {
    const isPitchDetectionDebuggingEnabled = settingsStore.getIsPitchDetectionDebuggingEnabled();

    if (!isPitchDetectionDebuggingEnabled) {
      notes = this.#filterOutNotesThatAreTooHighOrLow(notes);
    }

    this.#scoringWorker.postMessage({
      method: 'notesDetected',
      notes,
    });
  }

  /**
   * To improve pitch detection accuracy, ignore notes outside the range of expected notes for this segment plus or minus
   * an allowance set in this.#getNumberOfNotesOutsideLowestHighestToAllow()
   * NOTE: may want to do something more aggressive like filter out notes that are more than X notes away from expected
   * notes for the given time frame.
  */
  #filterOutNotesThatAreTooHighOrLow(notes: NoteEventTime[]): NoteEventTime[] {
    const highestAllowedNote = this.#getHighestScoreableNoteForSegment();
    console.log("highestAllowedNote: ", highestAllowedNote);
    const lowestAllowedNote = this.#getLowestScoreableNoteForSegment();
    console.log("lowestAllowedNote: ", lowestAllowedNote);
    return notes.filter((note) => note.pitchMidi >= lowestAllowedNote && note.pitchMidi <= highestAllowedNote);
  }

  #getHighestScoreableNoteForSegment(): number {
    const numberOfNotesOutsideMinMaxToAllow = this.#getNumberOfNotesOutsideLowestHighestToAllow();

    const highestNoteOnKeyboard = settingsService.getKeyboardKeysSettings().highestNote;
    let highestNoteInSegment: number|null = null;

    this.#notesInSegment?.forEach((note) => {
      if (highestNoteInSegment === null || note.midi > highestNoteInSegment) {
        highestNoteInSegment = note.midi;
      }
    });

    const highestNoteAllowed = highestNoteInSegment ? highestNoteInSegment + numberOfNotesOutsideMinMaxToAllow : highestNoteOnKeyboard;

    return Math.min(highestNoteAllowed || highestNoteOnKeyboard);
  }

  #getLowestScoreableNoteForSegment(): number {
    const numberOfNotesOutsideMinMaxToAllow = this.#getNumberOfNotesOutsideLowestHighestToAllow();

    const lowestNoteOnKeyboard =  settingsService.getKeyboardKeysSettings().lowestNote;
    let lowestNoteInSegment: number|null = null;

    this.#notesInSegment?.forEach((note) => {
      if (lowestNoteInSegment === null || note.midi < lowestNoteInSegment) {
        lowestNoteInSegment = note.midi;
      }
    });

    const highestNoteAllowed = lowestNoteInSegment ? lowestNoteInSegment - numberOfNotesOutsideMinMaxToAllow : lowestNoteOnKeyboard;

    return Math.max(highestNoteAllowed || lowestNoteOnKeyboard);
  }

  #getNumberOfNotesOutsideLowestHighestToAllow(): number {
    return 8;
  }

  /**
   * We support async getScore by using a hash of requestId to promise resolve/reject messages. This allows us to do
   * await getScore()
   */
  #handleWorkerScore(
    requestId: string,
    scoredNotes: IScoredNote[],
    unscoredNotesCriteriaInSelection: INoteScoreCriteria[],
    unscoredPlayerNotes: IPlayerNote[],
    speed: number,
    scoringStartTime: number | undefined,
    scoringEndTime: number,
    loopEndOffsetSeconds: number,
    seekOffsetSeconds: number
  ){
    const promise = this.#promiseHash[requestId];
    if(!promise){
      logger.lessonScorer.error({message: `no promise for scorer requestId: ${requestId}`});
      return ;
    }
    promise.resolve({scoredNotes, unscoredNotesCriteriaInSelection, unscoredPlayerNotes, speed, scoringStartTime, scoringEndTime, loopEndOffsetSeconds, seekOffsetSeconds});
    delete this.#promiseHash[requestId];
  }

  /**
   * Primary function that is invoked when the notesEvents.playbackStarted event is emitted.
   */
  async #beginScoring(
    segment: Segment,
    speed: number,
    speedRelativeToRecordingIndex: number,
    notesInSegment: Note[],
    seekOffsetSeconds: number,
    loopEndOffsetSeconds: number,
    shouldClearScoredNotes: boolean
  ){
    //clean up any scoring data without waiting for last notes of song to be scored.
    await this.#endScoringProcess(false);

    this.#didPlayerPlayAnyNotes = false;
    if(segment.hasVideo && (segment.mode === SegmentModeEnum.play || segment.mode === SegmentModeEnum.time || segment.mode === SegmentModeEnum.followAlong || segment.measures.length <= 0) ){
      logger.lessonScorerNote.debug({message: `noteScorer isn't responsible for scoring video only segments or follow along.`});
      return ;
    }
    logger.lessonScorerNote.debug({message: `noteScorer.beginScoring`});
    //clear the temp notes.  to do this, we need to hold onto the notesInSegment between calls to beginScoring, as the screen can only be cleared using their values.
    if(shouldClearScoredNotes){
      emitUiEventsToResetAllEditedNotesAndTempNotes(this.#notesInSegment);
    }

    this.#notesInSegment = notesInSegment;
    this.#numberOfTracks = determineHowManyTracksAreAvailable(this.#notesInSegment);
    this.#segment = segment;
    const {correctMsFloor, correctBeatDivision, incorrectMsFloor, incorrectBeatDivision} = thresholdSettings;
    const isPitchDetectionEnabled = lessonPlayerStore.getIsPitchDetectionEnabled();
    const isPitchDetectionDebuggingEnabled = settingsStore.getIsPitchDetectionDebuggingEnabled();

    this.#scoringWorker.postMessage({
      method: 'initialize',
      correctMsFloor,
      correctBeatDivision,
      incorrectMsFloor,
      incorrectBeatDivision,
      speed,
      speedRelativeToRecordingIndex,
      lessonDifficulty: segment.lessonDifficulty,
      measures: [...segment.measures],
      tempo: segment.tempo,
      noteScoreIntervalMs,
      isPitchDetectionEnabled,
      isPitchDetectionDebuggingEnabled,
    });

    this.#scoringWorker.postMessage({
      method: 'beginScoring',
      notesInSegment,
      seekOffsetSeconds,
      loopEndOffsetSeconds,
    });
  }

  /**
   * Responsible for ending the scoring process. It performs several tasks:
   * 1. It first checks if there is a segment currently being scored. If not, it logs a debug message and returns.
   * 2. It then logs a debug message indicating that the scoring process is ending and whether the user played any notes.
   * 3. The method then calls the `tellWorkerToStopScoringAndGetScore` method, which sends a message to the worker
   * thread to stop the scoring process and return the scoring results. This method returns a promise that resolves
   * with the scoring results.   *
   * 4. If the `waitForScoringToCompleteAndEmitScore` parameter is true, the method waits for the scoring process
   * to complete and then emits a `notesScoringCompleted` event with the scoring results. This event includes various
   * scoring data such as the scored notes, unscored notes, speed, scoring start and end times, and other related information.
   * 5. Finally, the method resets the `numberOfTracks` and `segment` properties to their initial states, indicating
   * that the scoring process has ended.
   *
   * This method is typically called when the playback of the musical segment ends, and it ensures that the scoring
   * process is properly terminated and the results are returned and handled appropriately.
   */
  async #endScoringProcess(waitForScoringToCompleteAndEmitScore= true){
    if(!this.#segment) {
      return logger.lessonScorerNote.debug({message: `noteScorer isn't scoring anything. Probably a video only segment.`});
    }

    logger.lessonScorerNote.debug({message: `noteScorer.endScoring. didPlayerPlayAnyNotes: ${this.#didPlayerPlayAnyNotes}`});

    const {
      scoredNotes,
      unscoredNotesCriteriaInSelection,
      unscoredPlayerNotes,
      speed, scoringEndTime,
      scoringStartTime,
      loopEndOffsetSeconds,
      seekOffsetSeconds
    } = await this.#tellWorkerToStopScoringAndGetScore(waitForScoringToCompleteAndEmitScore);

    const isPitchDetectionEnabled = lessonPlayerStore.getIsPitchDetectionEnabled();
    const isFeedbackEnabled = lessonPlayerStore.getIsInstantFeedbackEnabled();

    const shouldScoreNotes = this.#didPlayerPlayAnyNotes || (isPitchDetectionEnabled && isFeedbackEnabled);

    logger.pitchDetection.info({
      message: "noteScorer.endScoringProcess",
      showStack: false,
      data: [
        {key: "didPlayerPlayAnyNotes", value: this.#didPlayerPlayAnyNotes},
        {key: "isPitchDetectionEnabled", value: isPitchDetectionEnabled},
        {key: "isFeedbackEnabled", value: isFeedbackEnabled},
        {key: "shouldScoreNotes", value: shouldScoreNotes},
      ]
    });

    if(waitForScoringToCompleteAndEmitScore && this.#segment && scoringStartTime !== undefined && shouldScoreNotes) {
      emitNotesScoring_completed({
        unscoredNotesCriteriaInSelection,
        unscoredPlayerNotes,
        hands: this.#segment.hands,
        lessonId: this.#segment.lessonId,
        scoredNotes,
        segmentId: this.#segment.id,
        segmentMode: this.#segment.mode,
        speed,
        scoringStartTime,
        scoringEndTime,
        lessonDifficulty: this.#segment.lessonDifficulty,
        loopEndOffsetSeconds,
        seekOffsetSeconds
      });
    }
    this.#numberOfTracks = 0;
    this.#segment = undefined;
  }

  /**
   * Retrieve array of scoredNotes, unscoredNotesCriteria, and unscoredPlayerNotes.
   * Not sure if this will be needed once performance scorer is in place.
   * @param waitForScoringToComplete - whether the worker should wait a couple of intervals to complete scoring and send the score back.
   */
  async #tellWorkerToStopScoringAndGetScore(waitForScoringToComplete: boolean): Promise<{
    scoredNotes: IScoredNote[],
    unscoredNotesCriteriaInSelection: INoteScoreCriteria[],
    unscoredPlayerNotes: IPlayerNote[],
    speed: number,
    scoringStartTime?: number,
    scoringEndTime: number,
    loopEndOffsetSeconds: number,
    seekOffsetSeconds: number
  }> {
    const requestId = generateUUID();
    const promiseHash = this.#promiseHash;
    const promise = new Promise<{
      scoredNotes: IScoredNote[],
      unscoredNotesCriteriaInSelection: INoteScoreCriteria[],
      unscoredPlayerNotes: IPlayerNote[],
      speed: number,
      scoringStartTime?: number,
      scoringEndTime: number,
      loopEndOffsetSeconds: number,
      seekOffsetSeconds: number
    }>((resolve, reject) => {
      promiseHash[requestId] = {resolve, reject};
    });
    this.#scoringWorker.postMessage({method:'stopScoringAndGetScore', requestId, waitForScoringToComplete});
    return promise;
  }
}

/**
 * When multiple tracks/clefs are used (e.g. grad staff) we want to put extraneous notes in the correct track.
 * @param notes - notes in the segment.
 */
function determineHowManyTracksAreAvailable(notes: Note[]){
  const uniqueTracksSet = new Set();
  for(let i = 0; i < notes.length; ++i){
    const note = notes[i];
    uniqueTracksSet.add(note.path.trackIndex);
  }
  return uniqueTracksSet.size;
}

/**
 * Use scored notes to mark existing notes correct/incorrect/close and to display incorrect extraneous notes.
 * @param scoredNotes - array of scored notes which will be reflected in the UI
 * @param notesInSegment - to edit an existing note in the sheet music as green/red/etc, we need to find the scored note's corresponding note so we can access it's path (track, voice, beat, etc)
 * @param numberOfTracks - how many tracks/clefs are in the segment.
 */
function emitUiEventsForNotes(scoredNotes: IScoredNote[], notesInSegment: Note[], numberOfTracks: number){

  scoredNotes.forEach(scoredNote => {
    const matchingNote = notesInSegment.find(n => n.id === scoredNote.noteId);
    const isPitchDetectionDebuggingEnabled = settingsStore.getIsPitchDetectionDebuggingEnabled();

    const drawTempNoteHelper = (): void => {
      if(scoredNote.percentageIntoBar !== undefined && scoredNote.barIndex !== undefined && scoredNote.playerMidi !== undefined) {
        const track = determineWhichTrackToDrawTempNoteOn(scoredNote.playerMidi, numberOfTracks);

        soundSliceSdk_drawTempNote(
          scoredNote.barIndex,
          track,
          scoredNote.playerMidi,
          scoredNote.percentageIntoBar,
          noteScorerColors[scoredNote.score]
        );
      }
    };

    const isMidiDeviceConnected = lessonPlayerStore.getIsMidiConnected();
    const isLoopEnabled = lessonPlayerStore.getLoopEnabled();

    // Score matching notes if a midi device is connected
    // Don't score matching notes if a midi device is not connected and loop is enabled.
    const canUpdateMatchingNote = isMidiDeviceConnected
    || (!isMidiDeviceConnected && !isLoopEnabled && !isPitchDetectionDebuggingEnabled);

    if(matchingNote && canUpdateMatchingNote) {
      emitNote_changed(
        matchingNote.path.barIndex,
        matchingNote.path.trackIndex,
        matchingNote.path.voiceIndex,
        matchingNote.path.beatIndex,
        matchingNote.path.noteIndex,
        noteScorerColors[scoredNote.score]
      );

    } else if(scoredNote.percentageIntoBar !== undefined && scoredNote.barIndex !== undefined && scoredNote.playerMidi !== undefined) {
      drawTempNoteHelper();
    }

    if (isPitchDetectionDebuggingEnabled) {
      drawTempNoteHelper();
    }
  });
}

/**
 * When an extraneous note is played by the user, and there are 2 tracks/clefs available, we assume the second track is the bass clef, and put any notes below middle c (ie. 60) in the bass clef.
 */
function determineWhichTrackToDrawTempNoteOn(midi: number, numberOfTracks: number, ){
  return midi < 60 && numberOfTracks > 1 ? 1 : 0;
}

/**
 * Restore each note to its authored color, and clear any temp notes.
 */
function emitUiEventsToResetAllEditedNotesAndTempNotes(notes: Note[]){
  for(let i = 0; i < notes.length; ++i){
    const note = notes[i];
    emitNote_changed(note.path.barIndex, note.path.trackIndex, note.path.voiceIndex, note.path.beatIndex, note.path.noteIndex, note.color ?? "#000000");
  }
  soundSliceSdk_clearTempNotes();
}

/**
 * Workers typically need to be loaded as a separate script file, which is inconvenient.
 * We can get around this by stringifying the worker function and creating an object url.
 * @param func
 */
function createScoringWorkerFromFunction(func: Function){
  const dataObj = '(' + func + ')();';
  const blob = new Blob([dataObj]);
  const blobURL = URL.createObjectURL(blob);
  return new Worker(blobURL);
}

const noteScorer = new NoteScorer();
export default noteScorer;